import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DateTime } from 'luxon';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import { Classifications } from '../../../account-info-header/models';
import {
  selectClassifications,
  selectMasterAccount,
  selectSubAccountId,
} from '../../../account-info-header/reducers';
import {
  InternalTransfersActions,
  PtrsActions,
  TransactionFormActions,
} from '../../../create-request/actions';
import { ContributionReason } from '../../../create-request/models/internal-transfer/ContributionReason';
import { DistributionReason } from '../../../create-request/models/internal-transfer/DistributionReason';
import { OnetimeInputRequest } from '../../../create-request/models/internal-transfer/InternalTransferInputRequest';
import { ToAccountInfo } from '../../../create-request/models/internal-transfer/ToAccountInfo';
import { OptionDropdown, SelectControl } from '../../../create-request/models/OptionDropdownSelect';
import { selectInternalTransfersInfo, selectInternalTransfersToAccountProperties, selectIsFromEdit, selectIsOneTimeTotalDist, selectJournalsUseStandingAuth, selectLoadToAccountsError, selectPositionsLotsTableState, selectToAccountsInfo, selectToAccountsState, selectTransferType } from '../../../create-request/reducers';
import { InfoToAccount } from '../../../shared/models/InfoToAccount';
import { RemoveHyphenFromAccountPipe } from '../../../shared/pipes/remove-hyphen-from-account.pipe';
import { selectIsCreateTransaction } from '../../../shared/reducers';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { ComponentState } from '../../../shared/types/ComponentState';
import { Constants, toAccountErrorMap } from '../../../shared/utilities/Constants';
import { toAccountAllowedChars } from '../../../shared/utilities/regex-patterns';
import { isInternalTransferForm, TransactionForm } from '../../models/form/TransactionForm';
import { ComponentStateById, ToAccountInfoById, ToAccountProperties } from '../../models/internal-transfer/InternalTransfersInfo';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';

@Component({
  standalone: false,
  selector: 'mm-it-multiple-to-account',
  templateUrl: './it-multiple-to-account.component.html',
  styleUrls: ['./it-multiple-to-account.component.scss'],
})

export class MultipleToAccountComponent implements OnInit, OnDestroy {
  readonly contributionTypeTooltip = Constants.contributionYear;
  @Input() toAccountLimit: number = 1;
  @Input() parentForm: UntypedFormGroup;
  @Input() labelName: string = 'Receiving account number';
  @Input() isRecurringTransaction: boolean = false;
  @Input() disabled: boolean = false;
  @Input() info$: Observable<InfoToAccount>;
  @Output() resetForm = new EventEmitter<void>();
  @Output() toAccounttoDelete = new EventEmitter<string>();
  @Input() clearToAccounts: Observable<void>;
  @Input() datePickerControlName: string;
  @Input() editFormDetails: TransactionForm | undefined;
  @Input() sourcePtrsFieldsExist: boolean;
  @Input() downloadRequestFormError: boolean;
  isStandingAuthTransaction: boolean = false;
  toAccountValueOnClick: string;
  hasPositionsLotError: boolean;
  failedAccount: string;
  transferType: string;
  previousTransferType: string;
  eventsSubscription: Subscription;
  toAccountAllowedChars = toAccountAllowedChars;
  hyphenPipe = new RemoveHyphenFromAccountPipe;
  destroy$: Subject<boolean> = new Subject<boolean>();
  currentIndex: number;
  toAccountInContextList: { toAccount: string, InContext: boolean; }[] = [{ toAccount: '', InContext: false }];
  toAccountStates$: Observable<ComponentStateById>;
  toAccountValues: any[] = [];
  previousToAccountValues: any[] = [];
  subAccountId: string;
  oneTimeTotalDistSelected: boolean = false;
  masterAccount: string;
  accountClassifications: Classifications;
  toAccountInfo: ToAccountInfo;
  duplicateAccountNumber: string;
  showWarningIRA: boolean = false;
  iraRolloverContributionTypeWarningforMultiple: string = Constants.iraRolloverContributionTypeWarningforMultiple;
  warningText401kIneligableForEauth: string = Constants.accountTypeIneligibleForEauth;
  submit401kSeparatelyError: string = Constants.submit401kSeparatelyError;
  toAccountPropertiesById: { [accountId: string]: ToAccountInfo; } = {};
  isTrustNonPTRSAccountById: { [accountId: string]: boolean; } = {};
  contributionTypeModelById: { [accountId: string]: SelectControl; } = {};
  iraDistributionTypeModelById: { [accountId: string]: SelectControl; } = {};
  readonly controlNames = {
    toAccounts: 'toAccounts',
  };
  isCreateTransactionPage: boolean;
  iraContributionTypeModel: SelectControl = {
    title: 'IRA contribution type',
    controlName: 'iraContributionType',
    options: [],
    validationMessage: 'Contribution type is required'
  };
  distributionReasonModel: SelectControl = {
    title: 'Distribution reason',
    controlName: 'distributionReason',
    options: [],
    validationMessage: 'Distribution reason needs to be selected',
  };
  duplicateAccountNumberIndex: any;
  isEdit: boolean;
  multipleToAccounts: string[] = [];
  toAccountsInfoById: ToAccountInfoById;
  toAccountsList: string[] = [];
  isIraTransaction: boolean = false;
  showAddMultipleAccounts: boolean = false;
  isrestrictedAccount: string;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    public validationService: ValidateInputService,
    private featureFlagService: FeatureFlagService
  ) { }

  get toAccounts(): FormArray {
    return this.parentForm.get('toAccounts') as FormArray;
  }

  toAccount(toAccountIndex: number): UntypedFormGroup {
    return (this.parentForm.get('toAccounts') as FormArray).at(toAccountIndex) as UntypedFormGroup;
  }

  contributionType(toAccountIndex: number): FormControl {
    return this.toAccounts.at(toAccountIndex).get('iraContributionType') as FormControl;
  }

  distributionType(toAccountIndex: number): FormControl {
    return this.toAccounts.at(toAccountIndex).get('iraDistributionType') as FormControl;
  }

  ngOnInit(): void {
    this.toAccountStates$ = this.store.select(selectToAccountsState);
    this.parentForm.addControl('toAccounts', this.fb.array([]));
    this.toAccounts.setValidators([
      this.duplicateAccounts(),
      this.additional401kAccounts()
    ]);

    combineLatest([
      this.store.select(selectToAccountsInfo),
      this.store.select(selectIsFromEdit)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([_toAccounts, isEdit]) => {
      this.isEdit = isEdit;
      if (isEdit || this.toAccounts) {
        Object.keys(_toAccounts).forEach(toAccount => {
          this.multipleToAccounts.push(toAccount);
        });
        this.toAccountsInfoById = _toAccounts;
      }
    });

    this.store.select(selectJournalsUseStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.showAddMultipleAccounts = (value !== 'yes' && value !== 'bundled-yes');
      this.isStandingAuthTransaction = value === 'yes' || value === 'bundled-yes';
    });

    if (this.toAccounts.length < 1) {
      this.toAccounts.push(this.newToAccount());
    }

    this.store.select(selectIsCreateTransaction).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateTransactionPage = val);

    combineLatest([
      this.store.select(selectInternalTransfersInfo),
      this.store.select(selectIsFromEdit)
    ]).pipe(take(1)).subscribe(([info, isFromEdit]) => {
      this.isEdit = isFromEdit;
      if (this.toAccounts && info.inputCallFinished) {
        var iterator = 0;
        this.currentIndex = iterator;
        Object.keys(info.toAccountInfo).forEach((key) => {
          this.toAccounts?.at(iterator).get('toAccount')?.setValue(key);
          this.toAccountInContextList[iterator] = ({ toAccount: key, InContext: true });
          this.toAccountValues.push({ toAccount: key });
          Object.keys(info.toAccountInfo).forEach((toAccount) => {
            if (toAccount === key) {
              this.toAccountPropertiesById = {
                ...this.toAccountPropertiesById,
                [key]: info.toAccountInfo[toAccount],
              };
              if (this.isEdit && this.editFormDetails && isInternalTransferForm(this.editFormDetails)) {
                const transactionDetails = this.editFormDetails?.transactionDetails;
                const toAccounts = transactionDetails?.toAccounts ?? [];
                if (info?.iraFields[toAccount]?.isIraTransaction && info?.iraFields[toAccount]?.distributionReasonMap.length > 0) {
                  this.addIraDistributionTypeSelect(toAccounts[iterator]?.toAccount, info?.iraFields[toAccount]?.distributionReasonMap, toAccounts[iterator]?.iraDistributionType, true, iterator);
                }
                if (info?.toAccountPropertiesById[toAccount]?.iraContributionTypeMap.length > 0 && !this.is401k(iterator)) {
                  this.addContributionTypeSelect(toAccounts[iterator]?.toAccount, info?.toAccountPropertiesById[toAccount]?.iraContributionTypeMap, info?.positionsValidIraContributionCode);
                  this.showWarningIRA = true;
                }
                this.toAccounts?.at(iterator).get('trustIncomeToPercent')?.setValue(toAccounts[iterator]?.trustIncomeToPercent);
                this.toAccounts?.at(iterator).get('trustPrincipalToPercent')?.setValue(toAccounts[iterator]?.trustPrincipalToPercent);
                this.toAccounts?.at(iterator).get('trustDepositCode')?.setValue(toAccounts[iterator]?.trustDepositCode);
                this.toAccounts?.at(iterator).get('iraContributionType')?.setValue(toAccounts[iterator]?.iraContributionType);
                this.toAccounts?.at(iterator).get('iraDistributionType')?.setValue(toAccounts[iterator]?.iraDistributionType, { emitEvent: false });
              }
            }
          });
          this.toAccountsList.push(key);
          iterator++;
          if (this.multipleToAccounts.length !== this.toAccountsList.length) {
            this.addToAccount();
          }
          if (this.isStandingAuthTransaction) {
            this.disabled = true;
          }
        });
      }
    });

    if (this.disabled) {
      this.toAccounts.controls.forEach((control) => {
        control.get('toAccount')?.disable();
      });
    }

    this.store.select(selectIsOneTimeTotalDist).pipe(takeUntil(this.destroy$)).subscribe((isTotalDist) => {
      this.oneTimeTotalDistSelected = isTotalDist;
    });

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe((sub) => {
      this.subAccountId = sub;
    });

    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$)).subscribe((masterAccount) => {
      this.masterAccount = masterAccount;
    });

    this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe((classifications) => {
      this.accountClassifications = classifications;
    });

    this.store.select(selectTransferType).pipe(takeUntil(this.destroy$)).subscribe(type => {
      this.previousTransferType = this.transferType;
      this.transferType = (typeof type === 'string') ? type : "";
    });

    this.eventsSubscription = this.clearToAccounts?.subscribe(() => this.deleteToAccounts());

    combineLatest([
      this.info$,
      this.store.select(selectInternalTransfersToAccountProperties)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([info, propertiesById]) => {
      if (info?.inputCallFinished && this.toAccountInContextList?.[this.currentIndex] && this.toAccountInContextList?.[this.currentIndex]?.toAccount !== '') {
        this.toAccountInContextList[this.currentIndex].InContext = true;
      }
      this.toAccountPropertiesById = {
        ...this.toAccountPropertiesById,
        [info.toAccountInfo.accountNumber]: info.toAccountInfo
      };
      const accountNumber = info.toAccountInfo.accountNumber;
      const toAccountRegType = this.toAccountPropertiesById[accountNumber]?.regTypeShortName;

      //Only restrict if regType is i401(k) or Roth 401(k)
      if (info?.inputCallFinished && (toAccountRegType === 'I401(k)' || toAccountRegType === 'Roth I401(k)')) {
        this.handle401kAccountRestriction(accountNumber);
      }

      var toAccountProperties = propertiesById[info.toAccountInfo.accountNumber];
      if (toAccountProperties) {
        // Set PTRS ToAccount Fields
        this.isTrustNonPTRSAccountById = {
          ...this.isTrustNonPTRSAccountById,
          [info.toAccountInfo.accountNumber]: propertiesById[info.toAccountInfo.accountNumber].isTrustNonPTRSAccount
        };
        // Set Distribution Control
        if (toAccountProperties.inputCallFinished && toAccountProperties.inputIraFields.isIraTransaction && toAccountProperties.inputIraFields.distributionReasonMap.length > 0) {
          this.addIraDistributionTypeSelect(info.toAccountInfo.accountNumber, toAccountProperties.inputIraFields.distributionReasonMap);
        } else {
          this.parentForm?.removeControl('iraDistributionType');
        }

        // Set Contribution Control
        this.handleIraContributionTypeControl(info.toAccountInfo.accountNumber, toAccountProperties);
      }
    });

    this.store.select(selectPositionsLotsTableState).pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state.hasError) {
        this.hasPositionsLotError = this.toAccountInContextList.length === 1 && this.toAccountInContextList[0].toAccount === '' ? false : state.hasError;
        this.failedAccount = this.getFormIndexValue(this.currentIndex);
        this.resetToAccount(this.currentIndex, this.getFormIndexValue(this.currentIndex), false);
      } else {
        this.hasPositionsLotError = false;
      }
    });

    this.store.select(selectInternalTransfersToAccountProperties).pipe(takeUntil(this.destroy$)).subscribe(propertiesById => {
      Object.entries(propertiesById).forEach(([key, value]) => {
        if (value.inputIraFields.isIraTransaction && !this.isIraTransaction) {
          this.isIraTransaction = true;
        }
      });
    });

  }

  getToAccountState(toAccount: string): Observable<ComponentState> {
    return this.toAccountStates$.pipe(map(componentStateById => componentStateById[toAccount]));
  }

  AccountRegDetails(index: number): string[] {
    return this.toAccountPropertiesById[this.getFormIndexValue(index)]?.accountRegistration ?? [];
  }

  onFocus(event: any) {
    this.toAccountValueOnClick = event.target?.value;
  }

  AccountRegType(index: number): string {
    let regType = this.toAccountPropertiesById[this.getFormIndexValue(index)]?.regTypeShortName ?? '';
    if ((regType === 'I401(k)' || regType === 'Roth I401(k)')) {
      this.isrestrictedAccount = this.getFormIndexValue(index);
    }
    return regType;
  }

  is401k(index: number): boolean {
    let regType = this.toAccountPropertiesById[this.getFormIndexValue(index)]?.regTypeShortName ?? '';
    return regType === 'I401(k)' || regType === 'Roth I401(k)';
  }

  addToAccount() {
    if (!this.hasPositionsLotError) {
      this.toAccounts.push(this.newToAccount());
      this.toAccountInContextList.push({ toAccount: '', InContext: false });
    } else {
      return;
    }
  }

  newToAccount(): UntypedFormGroup {
    return this.fb.group({
      toAccount: ['', [
        Validators.required,
        Validators.minLength(Constants.toAccountInputLength),
        Validators.maxLength(Constants.toAccountMaxInputLength)]
      ],
      trustIncomeToPercent: [''],
      trustPrincipalToPercent: [''],
      trustDepositCode: [''],
      iraContributionType: [''],
    });
  }

  duplicateAccounts(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const values = control.value.map((c: any) => c.toAccount);
      const hasDuplicates = values.some(
        (value: string, index: number) => values.indexOf(value) !== index
      );
      if (hasDuplicates) {
        return { duplicateAccounts: true };
      }
      return null;
    };
  }

  additional401kAccounts(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value.length > 1) {
        for (let i = 0; i < control.value.length; i++) {
          if (this.is401k(i)) {
            return { additional401k: true };
          }
        };
      }
      return null;
    };
  }

  deleteToAccounts() {
    this.toAccounts.reset();
    this.resetForm.emit();
    this.showWarningIRA = false;
    this.toAccountInContextList = [{ toAccount: '', InContext: false }];
    this.toAccountPropertiesById = {};
    this.parentForm?.removeControl('iraDistributionType');
    this.parentForm.setControl('toAccounts', this.fb.array([]));
    this.toAccounts.push(this.newToAccount());
  }

  addIraDistributionTypeSelect(toAccount: string, iraDistributionTypeMap: DistributionReason[], distributionReason: string = '', isEdit: boolean = false, editToAccIterator?: number) {
    iraDistributionTypeMap = this.removeRMD7(iraDistributionTypeMap);
    this.iraDistributionTypeModelById = {
      ...this.iraDistributionTypeModelById,
      ...{
        [toAccount]: {
          title: 'Distribution reason',
          controlName: 'iraDistributionType',
          options: this.mapDistributionReasons(iraDistributionTypeMap),
          validationMessage: 'Distribution reason needs to be selected'
        }
      }
    };
    const existingFormGroup = ((this.parentForm.get('toAccounts') as FormArray).at(this.currentIndex) as UntypedFormGroup);
    if (!isEdit) {
      existingFormGroup?.addControl('iraDistributionType', new FormControl('', Validators.required));
      this.onDistributionTypeChanges(existingFormGroup);
    } else {
      const toAccountFormGroup = ((this.parentForm.get('toAccounts') as FormArray).at(editToAccIterator ?? 0) as UntypedFormGroup);
      toAccountFormGroup?.addControl('iraDistributionType', new FormControl('', Validators.required));
      this.onDistributionTypeChanges(toAccountFormGroup);
      toAccountFormGroup.get('iraDistributionType')?.setValue(distributionReason, { emitEvent: false });
    }
  }

  onDistributionTypeChanges(formGroup: UntypedFormGroup) {
    const control = formGroup.get('iraDistributionType');
    if (control) {
      control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        this.store.dispatch(
          TransactionFormActions.distributionReasonChangedMultipleIT()
        );
      });
    }
  }

  removeRMD7(iraDistributionTypeMap: DistributionReason[]): DistributionReason[] {
    if (this.transferType === "positionsCash" || this.transferType === "positions") {
      iraDistributionTypeMap.forEach((distributionType) => {
        if (distributionType.code.trim() === '7' && distributionType.subCode === 'RMD') {
          iraDistributionTypeMap = iraDistributionTypeMap.filter(item => item !== distributionType);
        }
      });
    }
    return iraDistributionTypeMap;
  }

  filterCurrentYearRolloverForNonIraToIra(contributionReasons: ContributionReason[], isIraAccount?: boolean): ContributionReason[] {
    if (this.accountClassifications?.isIra) {
      return contributionReasons;
    }

    if (!isIraAccount) {
      return contributionReasons;
    }

    const currentYear = DateTime.now().year.toString();

    return contributionReasons.filter(reason => {
      const description = reason.description?.toUpperCase() ?? '';
      const isRollover = description?.includes(Constants.iraRolloverLabel);
      if (!isRollover) {
        return true;
      }
      return reason.year === currentYear;
    });
  }

  addContributionTypeSelect(toAccount: string, contributionTypeMap: ContributionReason[], positionsValidIraContributionCode: string, isI401k: boolean = false, contributionReason: string = '') { //contributionReason parameter will be used in edit flow, leaving here for now even though it is not used yet
    const isIraAccount = this.toAccountPropertiesById[toAccount]?.isIraAccount;
    const filteredContributionReasons = this.filterCurrentYearRolloverForNonIraToIra(contributionTypeMap, isIraAccount);

    this.contributionTypeModelById = {
      ...this.contributionTypeModelById,
      ...{
        [toAccount]: {
          title: 'IRA contribution type',
          controlName: 'iraContributionType',
          options: isI401k ? this.mapI401kContributionReasons(filteredContributionReasons) : this.mapIraContributionReasons(filteredContributionReasons, positionsValidIraContributionCode),
          validationMessage: 'Contribution type is required'
        }
      }
    };

    const existingFormGroup = ((this.parentForm.get('toAccounts') as FormArray).at(this.currentIndex) as UntypedFormGroup);
    const isIraContributionTypePresent = existingFormGroup.get('iraContributionType');
    if (!this.isEdit || !isIraContributionTypePresent) {
      existingFormGroup.addControl('iraContributionType', new FormControl(''));
      existingFormGroup.get('iraContributionType')?.setValidators(Validators.required);
    }
  }

  mapIraContributionReasons(iraContributionType: ContributionReason[], positionsValidIraContributionCode: string): OptionDropdown[] {
    return iraContributionType.filter(elem => {
      return !!positionsValidIraContributionCode ? elem.code === positionsValidIraContributionCode && elem.description !== 'Select One' : elem.description !== 'Select One';
    }).map(element => new OptionDropdown(element.description, element.description));
  }

  mapI401kContributionReasons(contributionType: ContributionReason[]): OptionDropdown[] {
    return contributionType.map(element => new OptionDropdown(element.description, element.description));
  }

  mapDistributionReasons(iraDistributionType: DistributionReason[]): OptionDropdown[] {
    return iraDistributionType.filter(elem => elem.description !== 'Select One').map(element => new OptionDropdown(element.description, element.description));
  }

  handleIraContributionTypeControl(accountNumber: string, toAccountProperties: ToAccountProperties): void {
    if (toAccountProperties.iraContributionTypeMap.length > 0) {
      this.addContributionTypeSelect(accountNumber, toAccountProperties.iraContributionTypeMap, toAccountProperties.positionsValidIraContributionCode, this.is401k(this.currentIndex));
      this.showWarningIRA = (this.contributionTypeModelById[accountNumber]?.options.length > 0 && !this.is401k(this.currentIndex)) ? true : false;
    } else {
      this.parentForm?.removeControl('iraContributionType');
    }
  }

  reprocessIraContributionControlsAfterDeletion(): void {
    this.store.select(selectInternalTransfersToAccountProperties).pipe(take(1)).subscribe(propertiesById => {
      this.toAccounts.controls.forEach((control, index) => {
        const accountNumber = control.get('toAccount')?.value;
        if (accountNumber && propertiesById[accountNumber]) {
          this.currentIndex = index;
          this.handleIraContributionTypeControl(accountNumber, propertiesById[accountNumber]);
        }
      });
    });
  }

  getFormIndexValue(index: number): string {
    return this.toAccounts?.at(index)?.get('toAccount')?.value ?? "";
  }

  accountNumberExists(index: number): boolean {
    const value = this.getFormIndexValue(index);
    if (!value) return false;
    for (let i = 0; i < this.toAccounts.length; i++) {
      if (i !== index && this.getFormIndexValue(i) === value) {
        return true;
      }
    }
    return false;
  }

  deleteToAccount(index: number) {
    const newtoAccountPropertiesById: { [accountId: string]: ToAccountInfo; } = {};
    Object.entries(this.toAccountPropertiesById).forEach(([toAccount, toAccountInfo]) => {
      if (toAccount !== this.getFormIndexValue(index)) {
        newtoAccountPropertiesById[toAccount] = { ...toAccountInfo };
      } else if (this.duplicateAccountNumber === toAccount) {
        this.duplicateAccountNumber = '';
        newtoAccountPropertiesById[toAccount] = { ...toAccountInfo };
      }
    });
    this.toAccountPropertiesById = newtoAccountPropertiesById;
    this.toAccountInContextList.splice(index, 1);
    this.currentIndex = index;
    const value = this.getFormIndexValue(index);
    const accountNumberExists = this.accountNumberExists(index);
    if (value && !accountNumberExists) {
      this.toAccounttoDelete.emit(value);
      this.resetForm.emit();
    }
    if (this.failedAccount === value) {
      this.hasPositionsLotError = false;
    }
    this.resetRolloverBannerWarning(index);
    this.toAccounts.removeAt(index);
    this.toAccounts?.updateValueAndValidity();

    // Re-process IRA contribution controls for remaining accounts after deletion
    this.reprocessIraContributionControlsAfterDeletion();

    if (this.duplicateAccountNumberIndex !== null && this.duplicateAccountNumberIndex !== undefined && this.getFormIndexValue(this.duplicateAccountNumberIndex - 1)) {
      this.addIraDistributionAfterRemoveDuplicate(this.duplicateAccountNumberIndex - 1);
      this.onSubmit(this.duplicateAccountNumberIndex - 1);
      this.duplicateAccountNumberIndex = null;
    }
  }

  addIraDistributionAfterRemoveDuplicate(index: number) {
    if (this.isIraTransaction) {
      const existingFormGroup = ((this.parentForm.get('toAccounts') as FormArray).at(index) as UntypedFormGroup);
      existingFormGroup.addControl('iraDistributionType', new FormControl('', Validators.required));
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.eventsSubscription?.unsubscribe();
  }

  isToAccountValid(index: number) {
    return !this.disabled && (this.toAccounts.at(index).get('toAccount')?.valid ?? false);
  }

  toAccountFormIsInvalid(index: number) {
    if (this.accountNumberExists(index)) {
      this.duplicateAccountNumber = this.getFormIndexValue(index);
      this.duplicateAccountNumberIndex = index;
      return true;
    }
    if (this.is401k(index) && this.toAccounts.controls.length > 1) {
      return true;
    }
    return this.toAccounts.at(index).get('toAccount')!.touched && this.toAccounts.at(index).get('toAccount')!.invalid;
  }

  handle401kAccountRestriction(accountNumber: string) {
    const accountIndex = this.toAccounts.controls.findIndex(control =>
      control.get('toAccount')?.value === accountNumber
    );
    if (accountIndex !== -1 && this.toAccounts.length > 1) {
      // Collect removed account numbers
      const subsequentAccountNumbers = [];

      while (this.toAccounts.length > accountIndex + 1) {
        // Save the account number before removing
        const accountToRemove = this.toAccounts.at(accountIndex + 1).get('toAccount')?.value;
        if (accountToRemove) {
          subsequentAccountNumbers.push(accountToRemove);
        }

        // Remove the account from form and context list
        this.toAccounts.removeAt(accountIndex + 1);
        this.toAccountInContextList.splice(accountIndex + 1, 1);
      }

      // Clear positions for removed accounts
      subsequentAccountNumbers.forEach(account => {
        this.store.dispatch(InternalTransfersActions.itClearToAccount({ account }));
      });
    }
  }

  onSubmit(index: number, fromBlur = false): void {
    if (this.isEdit) {
      this.store.dispatch(TransactionFormActions.editLoadComplete());
    }

    this.store.select(selectLoadToAccountsError).pipe(takeUntil(this.destroy$)).subscribe((error) => {
      if (error) {
        this.scrollTo('it-download-request-form-error-banner');
      }
    });

    const value = this.getFormIndexValue(index);
    if (
      this.toAccounts?.at(index)?.get('toAccount')?.valid &&
      this.toAccounts.at(index).value.toAccount !== this.toAccountInContextList[index].toAccount
    ) {
      if (this.accountNumberExists(index)) {
        this.toAccounts.at(index).get('toAccount')?.markAsTouched();
        return;
      }
      let payload: OnetimeInputRequest = {
        IRAAccount: false,
        masterAccountId: this.masterAccount,
        subAccountId: this.subAccountId,
        bsloaTransaction: false,
        toAccountId: this.hyphenPipe.transform(this.toAccounts.at(index).value.toAccount),
        isRecurringTransaction: false,
        isCreateSloa: false,
        newRegTypeEnabled: true,
      };
      this.currentIndex = index;
      this.toAccountInContextList[index].toAccount = this.toAccounts.at(index).value.toAccount;
      this.callInput(payload, this.toAccounts.at(index).value.toAccount);
    } else if (fromBlur && this.toAccounts?.at(index)?.get('toAccount')?.invalid) {
      if (this.accountExistsAtOtherIndex(index, value) || this.toAccountInContextList[index].toAccount === '') {
        return;
      }
      this.resetToAccount(index, value, false);
      this.resetToAccountPropertiesByID(value, index);
    }
  }
  scrollTo(elemName: string) {
    setTimeout(() => {
      const bannerEl = document.getElementById(elemName);
      bannerEl?.focus();
      bannerEl?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    }, 100);
  }

  callInput(payload: OnetimeInputRequest, toAccount: string): void {
    if (this.isCreateTransactionPage) {
      this.store.dispatch(
        InternalTransfersActions.itToAccountEntered({ payload, toAccount })
      );
      if (this.accountClassifications.isPtrs && !this.sourcePtrsFieldsExist) {
        this.store.dispatch(
          PtrsActions.callPtrs({ accountId: this.subAccountId })
        );
      }
    }
  }

  resetToAccountPropertiesByID(toAccount: any, currentIndex?: number) {
    // Only delete if this account doesn't exist in any other row
    const accountExistsInOtherRow = this.toAccounts.controls.some((control, i) => 
      (currentIndex === undefined || i !== currentIndex) && control.get('toAccount')?.value === toAccount
    );
    
    if (!accountExistsInOtherRow) {
      delete this.toAccountPropertiesById[toAccount];
    }
  }

  accountExistsAtOtherIndex(index: number, accountNumber: string): boolean {
    for (let i = 0; i < this.toAccounts.length; i++) {
      if (i !== index && this.getFormIndexValue(i) === accountNumber) {
        return true;
      }
    }
    return false;
  }

  onInput(event: any, index: number): void {
    this.currentIndex = index;
    if (this.toAccountValues) {
      this.previousToAccountValues = this.toAccountValues;
    }
    this.toAccountValues = this.toAccounts?.value;
    let toAccountValue = this.toAccounts?.value[index]?.toAccount;
    if (!toAccountValue) {
      const value = this.toAccountInContextList[index].toAccount;
      let accountList = this.toAccountInContextList.map((account) => account.toAccount);
      this.resetToAccount(index, accountList[index], true);
      this.resetToAccountPropertiesByID(value, index);
      return;
    };
    const accountExistsElsewhere = this.accountExistsAtOtherIndex(index, toAccountValue);

    // Store the previous account value before any changes
    const previousAccountValue = this.previousToAccountValues[index]?.toAccount;
    const previousAccountLength = previousAccountValue?.replace("-", "").length || 0;

    if (previousAccountLength === Constants.minAccountNumberLength || previousAccountLength === Constants.maxAccountNumberLength) {
      if (accountExistsElsewhere) { 
        // Copy context from the existing account to this duplicate
        this.copyContextFromExistingAccount(index, toAccountValue);
      }
      else {
        this.resetToAccountAfterSwitch(index, toAccountValue);
        this.hasPositionsLotError = false;
      }
    } else if ((this.toAccountValueOnClick.replace("-", "").length === Constants.minAccountNumberLength 
                || this.toAccountValueOnClick.replace("-", "").length === Constants.maxAccountNumberLength)
                && this.toAccountValueOnClick.replace("-", "") !== event.target?.value.replace("-", "")) {
      //checks if account exists elsewhere before clearing
      const previousAccountExistsElsewhere = this.accountExistsAtOtherIndex(index, this.toAccountValueOnClick);
      if (!previousAccountExistsElsewhere) {
        this.store.dispatch(InternalTransfersActions.itClearToAccount({ account: this.toAccountValueOnClick }));
        this.resetToAccountPropertiesByID(this.toAccountValueOnClick, index);
      }
      this.hasPositionsLotError = false;
    } else if (previousAccountValue && previousAccountLength > 0 && previousAccountValue !== toAccountValue) {
      // Clear any previous account that was in context, even if it was invalid
      const accountInContext = this.toAccountInContextList[index].toAccount;
      if (accountInContext && accountInContext !== toAccountValue) {
        const previousAccountExistsElsewhere = this.accountExistsAtOtherIndex(index, accountInContext);
        if (!previousAccountExistsElsewhere) {
          this.store.dispatch(InternalTransfersActions.itClearToAccount({ account: accountInContext }));
          this.resetToAccountPropertiesByID(accountInContext, index);
        }
        this.hasPositionsLotError = false;
      }
    }

    toAccountValue = toAccountValue.replace(/[^\d]/g, '');
    const group = toAccountValue.match(/^(.{4})(.{1,5})(.*)/);

    if (group) {
      toAccountValue = `${group[1]}-${group[2]}`;
    }

    if (toAccountValue.length > 10) {
      toAccountValue = toAccountValue.substring(0, 9);
    }
    this.toAccounts?.at(index).get('toAccount')?.setValue(toAccountValue);

    // After setting the value, check if it's a complete account number and is a duplicate
    // If so, copy context from the existing account
    if (toAccountValue.replace("-", "").length === Constants.minAccountNumberLength || toAccountValue.replace("-", "").length === Constants.maxAccountNumberLength) {
      const isDuplicate = this.accountExistsAtOtherIndex(index, toAccountValue);
      if (isDuplicate) {
        this.copyContextFromExistingAccount(index, toAccountValue);
      }
    }

    this.reprocessIraContributionControlsAfterDeletion();
  }

  copyContextFromExistingAccount(currentIndex: number, accountNumber: string) {
    // Find the index that has this account and is already in context
    const existingIndex = this.toAccounts.controls.findIndex((control, i) => 
      i !== currentIndex && 
      control.get('toAccount')?.value === accountNumber &&
      this.toAccountInContextList[i]?.InContext
    );

    if (existingIndex !== -1) {
      // Copy the context state from the existing account to the current duplicate
      this.toAccountInContextList[currentIndex].toAccount = accountNumber;
      this.toAccountInContextList[currentIndex].InContext = true;

      // The toAccountPropertiesById is shared by account number, so both rows will access it
      // Copy IRA contribution type model if it exists
      if (this.contributionTypeModelById[accountNumber]) {
        this.contributionTypeModelById[accountNumber] = {
          ...this.contributionTypeModelById[accountNumber]
        };
      }

      // Copy IRA distribution type model if it exists
      if (this.iraDistributionTypeModelById[accountNumber]) {
        this.iraDistributionTypeModelById[accountNumber] = {
          ...this.iraDistributionTypeModelById[accountNumber]
        };
      }

      // Copy form control values from existing index (IRA contribution type, distribution type, etc.)
      const existingFormValues = this.toAccounts.at(existingIndex).value;
      const currentFormGroup = this.toAccounts.at(currentIndex) as UntypedFormGroup;
      
      // Add IRA contribution type control if it exists on the source
      if (existingFormValues.iraContributionType !== undefined && !currentFormGroup.get('iraContributionType')) {
        currentFormGroup.addControl('iraContributionType', new FormControl(existingFormValues.iraContributionType));
      }

      // Add IRA distribution type control if it exists on the source
      if (existingFormValues.iraDistributionType !== undefined && !currentFormGroup.get('iraDistributionType')) {
        currentFormGroup.addControl('iraDistributionType', new FormControl(existingFormValues.iraDistributionType, Validators.required));
      }
    }
  }

  //resetting toAccount when user switches toAccount without backspace OR when same toAccount is entered after switching cost basis method
  resetToAccountAfterSwitch(index: number, toAccountValue: any) {
    const hasValidIndex = index < this.previousToAccountValues.length;
    const previousToAccountExists = this.previousToAccountValues[index]?.toAccount;
    const isDifferentToAccount = toAccountValue !== this.previousToAccountValues[index]?.toAccount;
    const isDifferentTransferType = this.previousTransferType && this.previousTransferType !== this.transferType;
    if ((hasValidIndex && previousToAccountExists && isDifferentToAccount) || (isDifferentTransferType && !isDifferentToAccount)) {
      this.previousTransferType = this.transferType;
      const value = this.toAccountInContextList[index].toAccount;
      let accountList = this.toAccountInContextList.map((account) => account.toAccount);
      this.resetToAccount(index, accountList[index], true);
      this.resetToAccountPropertiesByID(value, index);
    }
  }

  // Checks if any row is having iraContributionType apart from removed index then shows warning banner
  isIraContributionTypePresent(i: number, index: number, group: FormGroup): boolean {
    let iraAccount = [];
    iraAccount.push(this.toAccountPropertiesById[group.get('toAccount')?.value]?.isIraAccount);
    if (i !== index && group?.controls && (group.get('toAccount')?.value && iraAccount.some(x => x === true))) {
      return true;
    }
    return false;
  }

  resetRolloverBannerWarning(index: number) {
    if (!this.accountClassifications.isIra) {
      let control = this.parentForm.get('toAccounts') as FormArray;
      let rolloverWarningBanner: boolean[] = [];
      control.controls.forEach((entry, i) => {
        let group = entry as FormGroup;
        if (this.isIraContributionTypePresent(i, index, group)) {
          rolloverWarningBanner.push(true);
        }
      });
      this.showWarningIRA = rolloverWarningBanner.some(x => x === true);
      rolloverWarningBanner = [];
    }
  }

  clearToAccountClicked(index: number): void {
    const value = this.toAccountInContextList[index].toAccount;
    this.resetToAccount(index, this.getFormIndexValue(index), true);
    this.resetToAccountPropertiesByID(value, index);
    this.toAccounts.at(index).get('toAccount')?.markAsTouched();
    this.toAccountFormIsInvalid(index);
    if (this.duplicateAccountNumberIndex !== null && this.getFormIndexValue(this.duplicateAccountNumberIndex)) {
      this.addIraDistributionAfterRemoveDuplicate(this.duplicateAccountNumberIndex);
      this.onSubmit(this.duplicateAccountNumberIndex);
      this.duplicateAccountNumberIndex = null;
    }
  }

  toAccountValidationError(index: number) {
    if (this.accountNumberExists(index)) {
      return 'Account number must be unique';
    }
    if (this.toAccounts && this.toAccounts.at(index).get('toAccount') && this.toAccounts.at(index).get('toAccount')?.errors) {
      const errorKeys: string[] = Object.keys(this.toAccounts.at(index)?.get('toAccount')?.errors ?? {});
      if (errorKeys.length > 0) {
        return toAccountErrorMap[
          errorKeys[0] as keyof typeof toAccountErrorMap
        ];
      }
    }
    if (this.is401k(index) && this.toAccounts.controls.length > 1) {
      return 'Submit account separately';
    }
  }

  receivingAccountValue(index: number): string {
    if (this.hasReceivingAccountDetails(index)) {
      return this.AccountRegDetails(index)[0];
    }
    return Constants.toAccountInfoUnavailable;
  }

  hasReceivingAccountDetails(index: number): boolean {
    return this.AccountRegDetails(index)?.length > 0;
  }

  resetToAccount(index: number, value: string, clearInvalidToAccount: boolean): void {
    // Check if this account exists in any other row before clearing shared data
    const accountExistsInOtherRow = this.toAccounts.controls.some((control, i) => 
      i !== index && control.get('toAccount')?.value === value
    );

    // Only clear shared data structures if the account doesn't exist in other rows
    if (!accountExistsInOtherRow) {
      const newIraContributionTypeModelById: { [accountId: string]: SelectControl; } = {};
      Object.entries(this.contributionTypeModelById).forEach(([toAccount, selectControl]) => {
        if (toAccount !== value) {
          newIraContributionTypeModelById[toAccount] = { ...selectControl };
        }
      });
      this.contributionTypeModelById = newIraContributionTypeModelById;

      const newtoAccountPropertiesById: { [accountId: string]: ToAccountInfo; } = {};
      Object.entries(this.toAccountPropertiesById).forEach(([toAccount, toAccountInfo]) => {
        if (toAccount !== value) {
          newtoAccountPropertiesById[toAccount] = { ...toAccountInfo };
        }
      });
      this.toAccountPropertiesById = newtoAccountPropertiesById;
      
      this.store.dispatch(InternalTransfersActions.itClearToAccount({ account: value }));
      this.toAccounttoDelete.emit(value);
      this.resetForm.emit();
    }

    // Always clear the specific row's context
    this.toAccountInContextList[index].InContext = false;
    this.toAccountInContextList[index].toAccount = '';
    if (clearInvalidToAccount) {
      this.toAccounts?.controls[index]?.reset();
      if (this.failedAccount === value) {
        this.hasPositionsLotError = false;
      }
    }
    this.resetRolloverBannerWarning(index);
    this.toAccounts?.controls[index].markAsDirty();
    this.toAccounts?.controls[index].markAsTouched();

    (this.toAccounts.at(index) as UntypedFormGroup).removeControl('iraContributionType');
    (this.toAccounts.at(index) as UntypedFormGroup).removeControl('iraDistributionType');
    (this.toAccounts.at(index) as UntypedFormGroup).patchValue(this.newToAccount());

    this.reprocessIraContributionControlsAfterDeletion();
  }
}
